import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  DialogService,
  EmptyComponent,
  InputComponent,
  SelectComponent,
  SkeletonComponent,
  TextareaComponent,
} from 'nebula-ui-kit';
import { finalize, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AdminTenantsService } from '../../../../data-access/src/lib/admin-users/admin-tenants.service';
import { PreservationMessagesService } from '../../../../data-access/src/lib/preservation-messages/preservation-messages.service';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import type {
  PreservationAudienceScope,
  PreservationMessagePayload,
  VaultPreservationMessage,
} from '../../../../domain/src/lib/preservation-messages/preservation-message.model';
import {
  tenantRoleLabel,
  VAULT_TENANT_ROLE_OPTIONS,
} from '../admin-users/admin-users.utils';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  openVaultConfirmDialog,
  toModuleAccessErrorMessage,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import {
  dashboardRoleStringImpliesVaultTenantAdmin,
  vaultUserCanTenantAdminFromLocalStorage,
} from '@nebula/shared-vault/data-access';

const DEFAULT_MESSAGES: Array<{ title: string; message: string }> = [
  {
    title: 'Tus archivos siguen protegidos',
    message:
      'Datavault monitorea tus expedientes para que permanezcan íntegros y disponibles. Aunque solo hagas consultas, cada acceso queda registrado para mantener la trazabilidad.',
  },
  {
    title: 'Preservación = confianza en el tiempo',
    message:
      'La preservación digital garantiza que los documentos consultados hoy mantengan su valor mañana. Por eso verificamos la integridad y autenticidad de los archivos antes de mostrarlos.',
  },
  {
    title: 'Recordatorio sobre copias maestras',
    message:
      'Cada archivo que ves proviene de una copia preservada con controles de seguridad y redundancia. Así aseguramos que la versión consultada corresponda al original autorizado.',
  },
];

@Component({
  selector: 'app-preservation-messages',
  templateUrl: './preservation-messages.component.html',
  styleUrls: ['./preservation-messages.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    GridFormComponent,
    GridFormItemComponent,
    FieldLabelComponent,
    ButtonComponent,
    EmptyComponent,
    SkeletonComponent,
    BadgeComponent,
    InputComponent,
    TextareaComponent,
    SelectComponent,
    CheckboxComponent,
    VaultPageAlertComponent,
  ],
})
export class PreservationMessagesComponent implements OnInit {
  private readonly api = inject(PreservationMessagesService);
  private readonly dashboard = inject(DashboardService);
  private readonly adminTenants = inject(AdminTenantsService);
  private readonly dialogService = inject(DialogService);

  /** Se recalcula en `ngOnInit` por si `user_data`/JWT se hidrataron tras el primer render (cookie → localStorage). */
  readonly canManage = signal(vaultUserCanTenantAdminFromLocalStorage());

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly busyId = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly items = signal<VaultPreservationMessage[]>([]);
  readonly tenantOptions = signal<Array<{ label: string; value: string }>>([]);

  readonly dialogOpen = signal(false);
  readonly editing = signal<VaultPreservationMessage | null>(null);

  readonly formTitle = signal('');
  readonly formMessage = signal('');
  readonly formAudienceScope = signal<PreservationAudienceScope>('all');
  readonly formAudienceRole = signal('');
  readonly formAudienceTenantId = signal('');
  readonly formStartsAt = signal('');
  readonly formEndsAt = signal('');
  readonly formActive = signal(true);

  readonly audienceOptions = [
    { label: 'Todos los usuarios', value: 'all' },
    { label: 'Por rol', value: 'role' },
    { label: 'Por empresa', value: 'tenant' },
  ];

  readonly audienceRoleOptions = VAULT_TENANT_ROLE_OPTIONS.map((o) => ({
    label: o.label,
    value: o.value,
  }));

  readonly defaultSuggested = DEFAULT_MESSAGES;

  readonly showAudienceRole = computed(() => this.formAudienceScope() === 'role');
  readonly showAudienceTenant = computed(() => this.formAudienceScope() === 'tenant');
  readonly showAudienceTarget = computed(
    () => this.showAudienceRole() || this.showAudienceTenant(),
  );

  onAudienceChange(v: unknown): void {
    const s = String(v ?? 'all');
    if (s === 'role' || s === 'tenant' || s === 'all') {
      this.formAudienceScope.set(s);
      if (s !== 'role') {
        this.formAudienceRole.set('');
      }
      if (s !== 'tenant') {
        this.formAudienceTenantId.set('');
      }
    }
  }

  onAudienceRoleChange(v: unknown): void {
    this.formAudienceRole.set(String(v ?? ''));
  }

  onAudienceTenantChange(v: unknown): void {
    this.formAudienceTenantId.set(String(v ?? ''));
  }

  onStartsAtInput(ev: Event): void {
    const t = ev.target as HTMLInputElement | null;
    this.formStartsAt.set(t?.value ?? '');
  }

  onEndsAtInput(ev: Event): void {
    const t = ev.target as HTMLInputElement | null;
    this.formEndsAt.set(t?.value ?? '');
  }

  constructor() {
    if (this.canManage()) {
      this.reload();
    } else {
      this.loading.set(false);
    }
  }

  ngOnInit(): void {
    const refreshIfNeeded = () => {
      const ok = vaultUserCanTenantAdminFromLocalStorage();
      if (!ok || this.canManage()) return;
      this.canManage.set(true);
      this.reload();
      this.loadTenantOptions();
    };
    refreshIfNeeded();
    queueMicrotask(refreshIfNeeded);
    setTimeout(refreshIfNeeded, 200);

    this.dashboard
      .getStats()
      .pipe(take(1), catchError(() => of(null)))
      .subscribe((stats) => {
        if (!stats?.role || !dashboardRoleStringImpliesVaultTenantAdmin(stats.role)) return;
        if (this.canManage()) return;
        this.canManage.set(true);
        this.reload();
        this.loadTenantOptions();
      });

    if (this.canManage()) {
      this.loadTenantOptions();
    }
  }

  private loadTenantOptions(): void {
    this.adminTenants
      .list()
      .pipe(catchError(() => of([])))
      .subscribe((rows) => {
        this.tenantOptions.set(
          rows
            .filter((t) => t.id && t.name)
            .map((t) => ({ label: t.name, value: t.id }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es')),
        );
      });
  }

  reload(): void {
    if (!this.canManage()) return;
    this.loading.set(true);
    this.error.set(null);
    this.api
      .list()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (rows) => this.items.set(rows),
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Mensajes Informativos',
              fallback: 'No se pudieron cargar los mensajes.',
            }),
          ),
      });
  }

  openCreate(): void {
    this.editing.set(null);
    this.resetForm();
    this.dialogOpen.set(true);
  }

  openEdit(msg: VaultPreservationMessage): void {
    this.editing.set(msg);
    this.formTitle.set(msg.title);
    this.formMessage.set(msg.message);
    this.formAudienceScope.set(msg.audience_scope);
    this.formAudienceRole.set(msg.audience_role ?? '');
    this.formAudienceTenantId.set(msg.tenant_id ?? '');
    this.formStartsAt.set(msg.starts_at ? msg.starts_at.slice(0, 16) : '');
    this.formEndsAt.set(msg.ends_at ? msg.ends_at.slice(0, 16) : '');
    this.formActive.set(msg.is_active);
    this.dialogOpen.set(true);
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
  }

  applySuggested(s: { title: string; message: string }): void {
    this.formTitle.set(s.title);
    this.formMessage.set(s.message);
  }

  private resetForm(): void {
    this.formTitle.set('');
    this.formMessage.set('');
    this.formAudienceScope.set('all');
    this.formAudienceRole.set('');
    this.formAudienceTenantId.set('');
    this.formStartsAt.set('');
    this.formEndsAt.set('');
    this.formActive.set(true);
  }

  private buildPayload(): PreservationMessagePayload {
    const scope = this.formAudienceScope();
    return {
      title: this.formTitle().trim(),
      message: this.formMessage().trim(),
      audience_scope: scope,
      audience_role: scope === 'role' ? this.formAudienceRole().trim() || null : null,
      tenant_id: scope === 'tenant' ? this.formAudienceTenantId().trim() || null : null,
      starts_at: this.formStartsAt()
        ? new Date(this.formStartsAt()).toISOString()
        : null,
      ends_at: this.formEndsAt() ? new Date(this.formEndsAt()).toISOString() : null,
      is_active: this.formActive(),
    };
  }

  save(): void {
    const payload = this.buildPayload();
    if (!payload.title || !payload.message) {
      this.error.set('Título y mensaje son obligatorios.');
      return;
    }
    if (payload.audience_scope === 'role' && !payload.audience_role) {
      this.error.set('Selecciona el rol destino.');
      return;
    }
    if (payload.audience_scope === 'tenant' && !payload.tenant_id) {
      this.error.set('Selecciona la empresa destino.');
      return;
    }
    this.error.set(null);
    this.saving.set(true);
    const ed = this.editing();

    const req = ed
      ? this.api.update(ed.id, payload)
      : this.api.create(payload);

    req.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: (row) => {
        if (ed) {
          this.items.update((list) => list.map((x) => (x.id === ed.id ? row : x)));
        } else {
          this.items.update((list) => [row, ...list]);
        }
        this.closeDialog();
      },
      error: (err: unknown) =>
        this.error.set(
          toModuleAccessErrorMessage(err, {
            moduleLabel: 'Mensajes Informativos',
            fallback: 'No se pudo guardar el mensaje.',
          }),
        ),
    });
  }

  toggleActive(msg: VaultPreservationMessage, checked: boolean): void {
    this.busyId.set(msg.id);
    this.api
      .update(msg.id, { is_active: checked })
      .pipe(
        finalize(() => this.busyId.set(null)),
        catchError(() => of(null)),
      )
      .subscribe({
        next: (row) => {
          if (row) {
            this.items.update((list) => list.map((x) => (x.id === msg.id ? row : x)));
          }
        },
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Mensajes Informativos',
              fallback: 'No se pudo actualizar el estado del mensaje.',
            }),
          ),
      });
  }

  deleteMessage(msg: VaultPreservationMessage): void {
    openVaultConfirmDialog(this.dialogService, {
      title: 'Eliminar mensaje',
      message: '¿Estás seguro de eliminar este mensaje?',
      confirmLabel: 'Eliminar',
      variant: 'error',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.busyId.set(msg.id);
      this.api
        .delete(msg.id)
        .pipe(finalize(() => this.busyId.set(null)))
        .subscribe({
          next: () => this.items.update((list) => list.filter((x) => x.id !== msg.id)),
          error: (err: unknown) =>
            this.error.set(
              toModuleAccessErrorMessage(err, {
                moduleLabel: 'Mensajes Informativos',
                fallback: 'No se pudo eliminar el mensaje.',
              }),
            ),
        });
    });
  }

  audienceBadgeLabel(msg: VaultPreservationMessage): string {
    if (msg.audience_scope === 'all') return 'Todos';
    if (msg.audience_scope === 'role') {
      const role = msg.audience_role?.trim();
      return role ? `Rol: ${tenantRoleLabel(role)}` : 'Rol: —';
    }
    const tenantId = msg.tenant_id?.trim();
    if (!tenantId) return 'Empresa específica';
    const match = this.tenantOptions().find((t) => t.value === tenantId);
    return match ? `Empresa: ${match.label}` : 'Empresa específica';
  }

  formatWhen(iso: string): string {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }
}
